import { useEffect } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { getBaseAndPrefix, isEnglishOnlyRoute, ROUTE_PREFIXES, stripAllLanguagePrefixes } from './routingUtils';

export function LangWrapper({ lang }: { lang: string }) {
  const { i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const { base } = getBaseAndPrefix(location.pathname);
    if (isEnglishOnlyRoute(base)) {
      if (i18n.language !== 'en') {
        void i18n.changeLanguage('en');
      }
      return;
    }

    let targetLang = 'en';
    if (lang.startsWith('en-')) targetLang = 'en';
    else if (lang.startsWith('it-')) targetLang = 'it';
    else if (lang.startsWith('nl-')) targetLang = 'nl';
    else if (lang.startsWith('es-')) targetLang = 'es';
    else if (lang.startsWith('tr-')) targetLang = 'tr';
    else if (lang.startsWith('fr-')) targetLang = 'fr';
    else if (lang.startsWith('de-')) targetLang = 'de';
    else if (lang.startsWith('pt-')) targetLang = 'pt';

    if (i18n.language !== targetLang) {
      void i18n.changeLanguage(targetLang);
    }
  }, [lang, i18n, location.pathname]);

  return <Outlet />;
}

export function LocalizedRedirector() {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  useEffect(() => {
    const path = location.pathname;

    // ── Redirect Engine: check for broken URLs before routing ──
    const { prefix, base } = getBaseAndPrefix(path);
    const currentLang = i18n.language || 'en';

    // 1. Check for canonical prefix casing and nested/duplicate language prefixes
    const cleanBase = stripAllLanguagePrefixes(path);
    const hasCanonicalPrefix = prefix && (path.startsWith(`/${prefix}/`) || path === `/${prefix}`);

    if (prefix && (!hasCanonicalPrefix || base !== cleanBase)) {
      const targetPath = `/${prefix}${cleanBase === '/' ? '' : cleanBase}`;
      void navigate(`${targetPath}${location.search}${location.hash}`, { replace: true });
      return;
    }

    if (isEnglishOnlyRoute(base)) {
      if (prefix) {
        void navigate(`${base}${location.search}${location.hash}`, { replace: true });
      }
      if (i18n.language !== 'en') {
        void i18n.changeLanguage('en');
      }
      return;
    }

    const hasLangPrefix = !!prefix;
    const expectedPrefix = ROUTE_PREFIXES[currentLang] || '';

    if (expectedPrefix && !hasLangPrefix) {
      void navigate(`/${expectedPrefix}${path === '/' ? '' : path}${location.search}${location.hash}`, {
        replace: true,
      });
    }
  }, [location.pathname, location.search, location.hash, i18n.language, navigate, i18n]);

  return null;
}
